import React, { ReactElement } from 'react';

import PropTypes from 'prop-types';

import { MultiControl, Selected, SearchInput, Placeholder } from '../layout';
import { MultiControlProps } from '../layout/Control/MultiControl';
import { GenericItem, GetComboboxProps } from '../types';

export type MultiAutocompleteControlProps<TItem extends GenericItem> = Omit<
  MultiControlProps<TItem>,
  'getComboboxProps'
> &
  GetComboboxProps & {
    openMenu: () => void;
  };

const MultiAutocompleteControl = <TItem extends GenericItem>({
  selectedItems,
  isOpen,
  isDisabled,
  disabled,
  getComboboxProps,
  openMenu,
  ...props
}: MultiAutocompleteControlProps<TItem>) => {
  disabled = disabled !== undefined ? disabled : isDisabled;

  const shouldShowPlaceholder =
    !isOpen && (!selectedItems || !selectedItems.length);

  return (
    <MultiControl
      selectedItems={selectedItems}
      isOpen={isOpen}
      getComboboxProps={() =>
        getComboboxProps({
          style: {
            flex: 1,
            display: 'flex',
            maxWidth: isOpen ? 'unset' : 0,
            opacity: isOpen ? 1 : 0,
          },
        })
      }
      renderItems={({ itemToString }) =>
        !isOpen && (
          <Selected selectedItems={selectedItems} itemToString={itemToString} />
        )
      }
      renderInput={(inputProps) => <SearchInput {...inputProps} />}
      renderPlaceholder={(placeholderProps) =>
        shouldShowPlaceholder && <Placeholder {...placeholderProps} />
      }
      onClick={() => {
        !isOpen && !disabled && openMenu();
      }}
      disabled={disabled}
      {...props}
    />
  );
};

MultiAutocompleteControl.displayName = 'MultiAutocompleteControl';

MultiAutocompleteControl.propTypes = {
  /** Function to open menu.  Provided by useMultiAutocomplete hook */
  openMenu: PropTypes.func.isRequired,
};

export default MultiAutocompleteControl as <T extends GenericItem>(
  p: MultiAutocompleteControlProps<T>
) => ReactElement;
